import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Package,
  Box,
  BarChart3,
  CreditCard,
  Wallet,
  Ship,
  ScanLine,
  UserCircle2,
  Users,
  Building2,
  ListOrdered,
  FileText,
  ArrowRight,
  TrendingUp,
  TrendingDown,
} from 'lucide-react';

function fmtInt(n) {
  return (Number(n) || 0).toLocaleString('en-BD');
}

function fmtRM(n) {
  const v = Number(n) || 0;
  return `RM ${v.toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function pctChange(current, previous) {
  if (!previous) return current > 0 ? '+100%' : '0%';
  const diff = ((current - previous) / previous) * 100;
  const sign = diff >= 0 ? '+' : '';
  return `${sign}${diff.toFixed(0)}%`;
}

function monthKey(d) {
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return '';
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`;
}

function statusLabel(status) {
  if (!status) return 'Unknown';
  return String(status).replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function AdminDashboard({
  apiBase,
  authToken,
  userRole,
  onNavigate,
  collections,
  aggregateStats,
  donutStyle,
  filteredRegularCollections,
  formatAdminDate,
  formatCurrencyRM,
  collectionRowTotalPrice,
  collectionStatusLabel,
}) {
  const [overview, setOverview] = useState(null);
  const [loadingOverview, setLoadingOverview] = useState(false);
  const [overviewError, setOverviewError] = useState('');

  const loadOverview = useCallback(async () => {
    if (!authToken) return;
    setLoadingOverview(true);
    setOverviewError('');
    try {
      const res = await fetch(`${apiBase}/api/admin/overview`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.message || 'Failed to load overview');
      setOverview(json.data || null);
    } catch (err) {
      setOverviewError(err.message || 'Failed to load overview');
    } finally {
      setLoadingOverview(false);
    }
  }, [apiBase, authToken]);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  const monthBuckets = useMemo(() => {
    const buckets = {};
    (collections || []).forEach((c) => {
      const key = monthKey(c.collectionDate || c.createdAt);
      if (!key) return;
      if (!buckets[key]) buckets[key] = { count: 0, revenue: 0 };
      buckets[key].count += 1;
      buckets[key].revenue += collectionRowTotalPrice(c);
    });
    return Object.entries(buckets)
      .sort(([a], [b]) => a.localeCompare(b))
      .slice(-6)
      .map(([key, val]) => ({
        key,
        label: new Date(`${key}-01`).toLocaleDateString('en-GB', { month: 'short', year: '2-digit' }),
        ...val,
      }));
  }, [collections, collectionRowTotalPrice]);

  const collectionMoM = useMemo(() => {
    const now = new Date();
    const thisKey = monthKey(now);
    const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const prevKey = monthKey(prev);
    let thisMonth = 0;
    let lastMonth = 0;
    (collections || []).forEach((c) => {
      const key = monthKey(c.collectionDate || c.createdAt);
      if (key === thisKey) thisMonth += 1;
      if (key === prevKey) lastMonth += 1;
    });
    return pctChange(thisMonth, lastMonth);
  }, [collections]);

  const revenueMoM = useMemo(() => {
    const now = new Date();
    const thisKey = monthKey(now);
    const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const prevKey = monthKey(prev);
    let thisMonth = 0;
    let lastMonth = 0;
    (collections || []).forEach((c) => {
      const key = monthKey(c.collectionDate || c.createdAt);
      const amt = collectionRowTotalPrice(c);
      if (key === thisKey) thisMonth += amt;
      if (key === prevKey) lastMonth += amt;
    });
    return pctChange(thisMonth, lastMonth);
  }, [collections, collectionRowTotalPrice]);

  const chartPoints = useMemo(() => {
    if (!monthBuckets.length) return null;
    const maxCount = Math.max(...monthBuckets.map((b) => b.count), 1);
    const maxRev = Math.max(...monthBuckets.map((b) => b.revenue), 1);
    const w = 400;
    const h = 180;
    const pad = 20;
    const step = monthBuckets.length > 1 ? (w - pad * 2) / (monthBuckets.length - 1) : 0;
    const countPts = monthBuckets.map((b, i) => {
      const x = pad + i * step;
      const y = h - pad - (b.count / maxCount) * (h - pad * 2);
      return `${x},${y}`;
    }).join(' ');
    const revPts = monthBuckets.map((b, i) => {
      const x = pad + i * step;
      const y = h - pad - (b.revenue / maxRev) * (h - pad * 2);
      return `${x},${y}`;
    }).join(' ');
    return { countPts, revPts, labels: monthBuckets.map((b) => b.label) };
  }, [monthBuckets]);

  const kpiCards = useMemo(() => {
    const fin = overview?.finance;
    const ship = overview?.shipments;
    const people = overview?.people;
    const inv = overview?.inventory;
    return [
      { title: 'Collections', value: fmtInt(overview?.collections?.total ?? aggregateStats.totalCollections), sub: `${overview?.collections?.pending ?? 0} pending`, change: collectionMoM, tone: 'purple', Icon: Package },
      { title: 'Packages', value: fmtInt(aggregateStats.totalPackages), sub: `${aggregateStats.totalCbm} CBM`, change: null, tone: 'blue', Icon: Box },
      { title: 'Collection revenue', value: formatCurrencyRM(aggregateStats.totalRevenue), sub: 'All time', change: revenueMoM, tone: 'red', Icon: CreditCard },
      { title: 'Net balance (MTD)', value: fin ? fmtRM(fin.netBalance) : '—', sub: 'This month', change: null, tone: 'green', Icon: Wallet },
      { title: 'Expenses (MTD)', value: fin ? fmtRM(fin.expenses) : '—', sub: fin ? `Income ${fmtRM(fin.totalIncome)}` : '', change: null, tone: 'orange', Icon: BarChart3 },
      { title: 'Shipments', value: fmtInt(ship?.total ?? 0), sub: `${ship?.inTransit ?? 0} in transit`, change: null, tone: 'blue', Icon: Ship },
      { title: 'Tracked packages', value: fmtInt(overview?.packages?.total ?? 0), sub: `${people?.shippers ?? 0} shippers`, change: null, tone: 'purple', Icon: ScanLine },
      { title: 'People', value: fmtInt((people?.shippers ?? 0) + (people?.consignees ?? 0)), sub: `${people?.consignees ?? 0} consignees`, change: null, tone: 'green', Icon: UserCircle2 },
      { title: 'Box types', value: fmtInt(inv?.emptyBoxTypes ?? 0), sub: inv?.lowStock ? `${inv.lowStock} low stock` : `${inv?.boxItems ?? 0} items`, change: null, tone: 'orange', Icon: ListOrdered },
      { title: 'Agents', value: fmtInt(overview?.shippingAgents ?? 0), sub: overview?.users ? `${overview.users.staff + overview.users.admin} staff` : 'Shipping agents', change: null, tone: 'red', Icon: Building2 },
    ];
  }, [overview, aggregateStats, collectionMoM, revenueMoM, formatCurrencyRM]);

  const shipmentStatusRows = useMemo(() => {
    const s = overview?.shipments;
    if (!s) return [];
    return [
      { label: 'Pending', count: s.pending, tone: 'warn' },
      { label: 'Processing', count: s.processing, tone: 'warn' },
      { label: 'In transit', count: s.inTransit, tone: 'blue' },
      { label: 'Delivered', count: s.delivered, tone: 'success' },
    ];
  }, [overview]);

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
      {overviewError ? (
        <p className="jet-finance-error" style={{ marginBottom: '1rem' }}>{overviewError}</p>
      ) : null}

      <div className="jet-kpi-grid jet-kpi-grid-wide">
        {kpiCards.map((k) => {
          const Ic = k.Icon;
          const changeUp = k.change && !String(k.change).startsWith('-');
          return (
            <div key={k.title} className="jet-kpi-card">
              <div className="jet-kpi-top">
                <div className="jet-kpi-title">{k.title}</div>
                <div className={`jet-kpi-icon ${k.tone}`}>
                  <Ic className="w-5 h-5" />
                </div>
              </div>
              <div className="jet-kpi-value">{loadingOverview && !overview ? '…' : k.value}</div>
              {k.change ? (
                <div className={`jet-kpi-change ${changeUp ? 'up' : 'down'}`}>
                  {changeUp ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
                  {k.change} vs last month
                </div>
              ) : (
                <div className="jet-kpi-change muted">{k.sub}</div>
              )}
            </div>
          );
        })}
      </div>

      <div className="jet-dash-mid">
        <div className="jet-card">
          <div className="jet-card-title">
            Collections trend
            <span className="jet-dash-chart-legend">
              <span className="jet-dash-legend-line blue" /> Count
              <span className="jet-dash-legend-line green dashed" /> Revenue
            </span>
          </div>
          <div className="jet-line-chart">
            {chartPoints ? (
              <>
                <svg viewBox="0 0 400 200" preserveAspectRatio="none">
                  <polyline fill="none" stroke="#cbd5e1" strokeWidth="1" points="20,180 380,180" />
                  <polyline fill="none" stroke="#1e40af" strokeWidth="2.5" strokeLinejoin="round" points={chartPoints.countPts} />
                  <polyline fill="none" stroke="#16a34a" strokeWidth="2.5" strokeLinejoin="round" strokeDasharray="4 3" points={chartPoints.revPts} />
                </svg>
                <div className="jet-dash-chart-labels">
                  {chartPoints.labels.map((l) => (
                    <span key={l}>{l}</span>
                  ))}
                </div>
              </>
            ) : (
              <p className="jet-dash-empty">Add collections to see monthly trends.</p>
            )}
          </div>
        </div>

        <div className="jet-card">
          <div className="jet-card-title">Finance (this month)</div>
          {overview?.finance ? (
            <div className="jet-dash-stat-list">
              <div className="jet-dash-stat-row">
                <span>Collection income</span>
                <strong>{fmtRM(overview.finance.collectionIncome)}</strong>
              </div>
              <div className="jet-dash-stat-row">
                <span>Manual income</span>
                <strong>{fmtRM(overview.finance.manualIncome)}</strong>
              </div>
              <div className="jet-dash-stat-row">
                <span>Payments received</span>
                <strong>{fmtRM(overview.finance.paymentsReceived)}</strong>
              </div>
              <div className="jet-dash-stat-row">
                <span>Expenses</span>
                <strong className="neg">{fmtRM(overview.finance.expenses)}</strong>
              </div>
              <div className="jet-dash-stat-row total">
                <span>Net balance</span>
                <strong>{fmtRM(overview.finance.netBalance)}</strong>
              </div>
              <button type="button" className="jet-btn jet-btn-secondary jet-dash-link-btn" onClick={() => onNavigate('finance')}>
                Open finance <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <p className="jet-dash-empty">{loadingOverview ? 'Loading…' : 'Finance data unavailable.'}</p>
          )}
        </div>

        <div className="jet-card">
          <div className="jet-card-title">Shipments & packages</div>
          <div className="jet-dash-stat-list">
            {shipmentStatusRows.map((row) => (
              <div key={row.label} className="jet-dash-stat-row">
                <span>{row.label}</span>
                <span className={`jet-badge jet-badge-${row.tone}`}>{row.count}</span>
              </div>
            ))}
            {(overview?.packages?.byStatus || []).slice(0, 4).map((row) => (
              <div key={row.status} className="jet-dash-stat-row">
                <span>{statusLabel(row.status)}</span>
                <strong>{row.count}</strong>
              </div>
            ))}
            <button type="button" className="jet-btn jet-btn-secondary jet-dash-link-btn" onClick={() => onNavigate('shipments')}>
              Open shipments <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      <div className="jet-dash-mid jet-dash-mid-2">
        <div className="jet-card">
          <div className="jet-card-title">Recent collections</div>
          <div className="jet-recent-list">
            {filteredRegularCollections.slice(0, 5).map((c) => {
              const st = collectionStatusLabel(c);
              return (
                <div key={c._id || c.collectionNo} className="jet-recent-item">
                  <div className="jet-recent-icon">
                    <FileText className="w-5 h-5" />
                  </div>
                  <div className="jet-recent-meta">
                    <strong>{c.collectionNo || '—'}</strong>
                    <span>{formatAdminDate(c.collectionDate || c.createdAt)}</span>
                  </div>
                  <div className="jet-recent-right">
                    <span className={`jet-badge ${st === 'Completed' ? 'jet-badge-success' : 'jet-badge-warn'}`}>{st}</span>
                    <div style={{ fontWeight: 700, marginTop: 4 }}>{formatCurrencyRM(collectionRowTotalPrice(c))}</div>
                  </div>
                </div>
              );
            })}
            {collections.length === 0 && (
              <p className="jet-dash-empty">No collections yet.</p>
            )}
          </div>
        </div>

        <div className="jet-card">
          <div className="jet-card-title">Recent shipments</div>
          <div className="jet-recent-list">
            {(overview?.recent?.shipments || []).map((s) => (
              <div key={s._id} className="jet-recent-item">
                <div className="jet-recent-icon">
                  <Ship className="w-5 h-5" />
                </div>
                <div className="jet-recent-meta">
                  <strong>{s.workspace?.shipmentCode || s.trackingNumber || '—'}</strong>
                  <span>{formatAdminDate(s.updatedAt || s.createdAt)}</span>
                </div>
                <div className="jet-recent-right">
                  <span className="jet-badge jet-badge-blue">{statusLabel(s.status)}</span>
                </div>
              </div>
            ))}
            {!loadingOverview && !(overview?.recent?.shipments || []).length && (
              <p className="jet-dash-empty">No shipments yet.</p>
            )}
          </div>
        </div>

        <div className="jet-card">
          <div className="jet-card-title">Operations snapshot</div>
          <div className="jet-dash-stat-list">
            <div className="jet-dash-stat-row">
              <span>Completed collections</span>
              <strong>{fmtInt(overview?.collections?.completed ?? 0)}</strong>
            </div>
            <div className="jet-dash-stat-row">
              <span>This month</span>
              <strong>{fmtInt(overview?.collections?.thisMonth ?? 0)}</strong>
            </div>
            <div className="jet-dash-stat-row">
              <span>Consolidated bills</span>
              <strong>{fmtInt(overview?.collections?.consolidated ?? 0)}</strong>
            </div>
            <div className="jet-dash-stat-row">
              <span>Shippers</span>
              <strong>{fmtInt(overview?.people?.shippers ?? 0)}</strong>
            </div>
            <div className="jet-dash-stat-row">
              <span>Consignees</span>
              <strong>{fmtInt(overview?.people?.consignees ?? 0)}</strong>
            </div>
            {userRole === 'admin' && overview?.users ? (
              <>
                <div className="jet-dash-stat-row">
                  <span>Portal users</span>
                  <strong>{fmtInt(overview.users.total)}</strong>
                </div>
                <div className="jet-dash-stat-row">
                  <span>Active users</span>
                  <strong>{fmtInt(overview.users.active)}</strong>
                </div>
              </>
            ) : null}
            <button type="button" className="jet-btn jet-btn-secondary jet-dash-link-btn" onClick={() => onNavigate('customers')}>
              Open customers <Users className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="jet-card">
          <div className="jet-card-title">Top box types</div>
          <div className="jet-donut-wrap">
            <div className="jet-donut" style={{ background: donutStyle.background }}>
              <div className="jet-donut-hole">
                <strong>{aggregateStats.totalPackages.toLocaleString('en-BD')}</strong>
                <span>Total packages</span>
              </div>
            </div>
            <div className="jet-legend">
              {donutStyle.legend.map((row) => (
                <div key={row.key} className="jet-legend-row">
                  <span className="jet-legend-dot" style={{ background: row.color }} />
                  <span style={{ flex: 1 }}>{row.key}</span>
                  <span style={{ fontWeight: 600 }}>{row.val}</span>
                </div>
              ))}
              {donutStyle.legend.length === 0 && (
                <div className="jet-legend-row jet-dash-empty">Add collections to see distribution</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
